/**
 * The Yajna ground at Sri Venkateswara Temple: 51 Kunds in four columns
 * running back from the stage, holding 13, 14, 14 and 10. A Kund is named
 * by its column and how far back it is: A1 to A13, B1 to B14, C1 to C14,
 * D1 to D10, so a place is that code ("B7"). Behind the codes, numbers run
 * across each row from the left, starting at the stage: the front row is
 * 1, 2, 3, 4 across columns A to D, the row behind it is 5 to 8. A row
 * skips a column that has ended, so row 11 is 41, 42, 43 across A to C. One group sits at each Kund, so a place is the Kund number
 * ("7"). The side codes ("7A") remain parseable for the older layout.
 */

export type Side = "A" | "B";

/** The columns left to right as you face the stage, and how many Kunds
 *  each holds, front to back. */
const COLUMNS = [
  { label: "A", kunds: 13 },
  { label: "B", kunds: 14 },
  { label: "C", kunds: 14 },
  { label: "D", kunds: 10 },
] as const;
/** The longest column, which sets how far back the ground runs. */
const DEEPEST = Math.max(...COLUMNS.map((c) => c.kunds));

/** Which half of the Kund each side is, as you face the stage. */
export const SIDE_HALF = { A: "left", B: "right" } satisfies Record<
  Side,
  "left" | "right"
>;

export interface Kund {
  /** What the Kund is called: its column and position, "B7". */
  code: string;
  /** Its order on the ground, row by row from the stage. Bookings placed
   *  before the codes existed are stored under this number. */
  number: number;
  /** The column's letter, left to right as you face the stage. */
  column: string;
  /** The row: 1 at the stage, counting back. */
  position: number;
  sides: readonly Side[];
  x: number;
  y: number;
}

export interface Place {
  /** The Kund's code: "B7". */
  code: string;
  kund: Kund;
  side: Side | undefined;
}

const FIRST_ROW_Y = 215;
const ROW_GAP = 80;
const COLUMN_GAP = 190;
const LAST_ROW_Y = FIRST_ROW_Y + (DEEPEST - 1) * ROW_GAP;

export const MAP = {
  width: 1000,
  height: LAST_ROW_Y + 120,
  tile: 48,
  columnGap: COLUMN_GAP,
  rowGap: ROW_GAP,
  stage: { x: 240, y: 40, width: 520, height: 84 },
  // People come in at the front left, beside A1, at the head of the left
  // walkway.
  entrance: { x: 120, y: FIRST_ROW_Y - 75 },
  aisle: { left: 120, right: 880, bottom: LAST_ROW_Y + 68 },
  /** Column letters sit under the stage and again above the entrance. */
  columnLabel: { top: FIRST_ROW_Y - 68, bottom: LAST_ROW_Y + 36 },
} as const;

const columnX = (index: number): number =>
  MAP.width / 2 + (index - (COLUMNS.length - 1) / 2) * COLUMN_GAP;

export const kunds: readonly Kund[] = Array.from(
  { length: DEEPEST },
  (_, rowIndex) =>
    COLUMNS.flatMap((column, columnIndex) =>
      // A column that has ended leaves a gap in the rows behind it.
      rowIndex < column.kunds
        ? [{ column: column.label, columnIndex, position: rowIndex + 1 }]
        : [],
    ),
)
  .flat()
  .map((spot, index) => ({
    code: `${spot.column}${spot.position}`,
    number: index + 1,
    column: spot.column,
    position: spot.position,
    sides: [],
    x: columnX(spot.columnIndex),
    y: FIRST_ROW_Y + (spot.position - 1) * ROW_GAP,
  }));

export const columnLabels: readonly { label: string; x: number }[] =
  COLUMNS.map((column, index) => ({ label: column.label, x: columnX(index) }));

/** The walkways running back from the stage: either side, and between columns. */
export const walkways: readonly number[] = [
  MAP.aisle.left,
  ...COLUMNS.slice(1).map(
    (_, index) => (columnX(index) + columnX(index + 1)) / 2,
  ),
  MAP.aisle.right,
];

const kundsByNumber = new Map(kunds.map((kund) => [kund.number, kund]));
const kundsByCode = new Map(kunds.map((kund) => [kund.code, kund]));

export function placeCode(kund: Kund, side: Side | undefined): string {
  return `${kund.code}${side ?? ""}`;
}

/**
 * Accepts case, spacing and an optional "Kund" prefix; rejects unlisted
 * places. A plain number is the Kund's order on the ground, which is how
 * places were stored and linked before the codes.
 */
export function findPlace(input: string): Place | undefined {
  const text = input.trim().toUpperCase();
  const coded = /^(?:KUND\s*)?([A-Z])\s*(\d{1,2})$/.exec(text);
  if (coded) {
    const named = kundsByCode.get(`${coded[1]}${Number(coded[2])}`);
    return named && { code: named.code, kund: named, side: undefined };
  }
  const match = /^(?:KUND\s*)?(\d{1,2})\s*([AB])?$/.exec(text);
  if (!match) {
    return undefined;
  }
  const kund = kundsByNumber.get(Number(match[1]));
  if (!kund) {
    return undefined;
  }
  const side = match[2] === "A" || match[2] === "B" ? match[2] : undefined;
  if (side && !kund.sides.includes(side)) {
    return undefined;
  }
  return { code: placeCode(kund, side), kund, side };
}

/** The walkway beside a Kund's column: the outer side for the outer columns. */
function walkwayFor(kund: Kund): number {
  const index = columnLabels.findIndex((c) => c.label === kund.column);
  const leftHalf = index < columnLabels.length / 2;
  return walkways[leftHalf ? index : index + 1] ?? MAP.aisle.left;
}

/** SVG path from the entrance at the front left, across the front of the
 *  ground to the walkway beside the column, and back to the Kund. */
export function routeTo(kund: Kund): string {
  const walkway = walkwayFor(kund);
  const { entrance } = MAP;
  const front = FIRST_ROW_Y - ROW_GAP / 2;
  const edge = kund.x + (walkway < kund.x ? -1 : 1) * (MAP.tile / 2 + 6);
  return [
    `M ${entrance.x} ${entrance.y}`,
    `V ${front}`,
    `H ${walkway}`,
    `V ${kund.y}`,
    `H ${edge}`,
  ].join(" ");
}

const ORDINALS = ["first", "second", "third", "fourth"];

function nth(n: number): string {
  const tens = n % 100;
  const suffix =
    tens >= 11 && tens <= 13
      ? "th"
      : (["th", "st", "nd", "rd"][n % 10] ?? "th");
  return `${n}${suffix}`;
}

export function directionTo(kund: Kund): string {
  const index = columnLabels.findIndex((c) => c.label === kund.column);
  const column = `Column ${kund.column} is the ${ORDINALS[index] ?? ""} from the left as you face the stage.`;
  return kund.position === 1
    ? `${column} Your Kund is at the front, just below the stage.`
    : `${column} Your Kund is the ${nth(kund.position)} back from the stage.`;
}

/** Every Kund's code, in order on the ground: row by row from the stage. */
export const KUND_CODES: readonly string[] = kunds.map((k) => k.code);

/** Orders two places as they sit on the ground, front row first. */
export function compareKundPlaces(a: string, b: string): number {
  return (findPlace(a)?.kund.number ?? 0) - (findPlace(b)?.kund.number ?? 0);
}

/** The stored place for a booking, or undefined for an unlisted one. */
export function normaliseKundPlace(input: string): string | undefined {
  return findPlace(input)?.code;
}

/**
 * The Kunds a booking sits at, from its stored place. A group big enough
 * to split is stored on two Kunds side by side ("A2,B2"); everyone else on
 * one ("B7"). Unlisted places are dropped.
 */
export function kundPlacesOf(stored: string | null | undefined): string[] {
  if (!stored) {
    return [];
  }
  return stored
    .split(",")
    .flatMap((part) => normaliseKundPlace(part) ?? [])
    .sort(compareKundPlaces);
}

/** How a booking's Kunds are stored: "B7", or "A2,B2" for a split group. */
export function storedKundPlace(places: readonly string[]): string | null {
  return places.length === 0 ? null : places.join(",");
}

/** "Kund B7", or "Kunds A2 and B2" for a split group. */
export function kundPlaceLabel(stored: string | null | undefined): string {
  const places = kundPlacesOf(stored);
  return places.length > 1
    ? `Kunds ${places.join(" and ")}`
    : `Kund ${places[0] ?? ""}`.trim();
}

/**
 * The Kund beside this one in the same row, for a group that takes two:
 * the next one along, or the one before at the end of a row.
 */
export function kundBeside(place: string): string | undefined {
  const kund = findPlace(place)?.kund;
  if (!kund) {
    return undefined;
  }
  const inRow = (n: number) => kundsByNumber.get(n)?.position === kund.position;
  const beside = inRow(kund.number + 1) ? kund.number + 1 : kund.number - 1;
  return inRow(beside) ? kundsByNumber.get(beside)?.code : undefined;
}

/** The map page for one place, ready to put in an email. */
export function kundMapUrl(siteUrl: string, place: string): string {
  return `${siteUrl}/events/hawan-yajna-sep-19/map?place=${encodeURIComponent(place)}`;
}
